import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  parseNumberByPrecision,
  ReplaceVirtualTranslationKey,
  truncWithPrecision,
} from "../../../../../helpers";

import { BetTicketContentFooter } from "./bet-ticket-content-footer";
import { TicketConfig } from "../../../../../models/partnerResponse";
import { useStore, useTranslation } from "../../../../../hooks";
import { BetTypeNames } from "../../../../../constants";
import { TicketAccepted } from "../../../../../models";
import { BetInfo } from "../ticket/bet-info";
import { Market } from "../ticket/market";
import { Ticket } from "../ticket";

interface BetTicketProps {
  isExpress: boolean;
  data: TicketAccepted;
  ticketConfig: TicketConfig;
}

const BetTicket: React.FC<BetTicketProps> = observer(
  ({ data, isExpress, ticketConfig }) => {
    const t = useTranslation();

    const {
      translationsStore: { selectedLanguageCode },
      ticketsStore,
      partnerStore: { isPinRequiredForPayout },
    } = useStore();

    const {
      barcode,
      date,
      ticketInfo,
      betType,
      amount,
      possibleWin,
      ticketId,
      betId,
      gameId,
      userName,
    } = data;

    const [qrImgLoaded, setQrImgLoaded] = useState(false);
    const [ticketLoaded, setTicketLoaded] = useState(false);
    const [imgImageError, setImgImageError] = useState<any>(false);

    useEffect(() => {
      if (+barcode && !qrImgLoaded) return;

      if (ticketLoaded || imgImageError || ticketConfig?.logo === null) {
        if (isPinRequiredForPayout) {
          ticketsStore.printTicket(ticketId).then((res) => {
            window.print();
          });
        } else {
          window.print();
        }
      }
    }, [ticketLoaded, imgImageError, qrImgLoaded, barcode]);

    const firstMarketData = useMemo(() => {
      const [firstMarket] = ticketInfo;
      return firstMarket;
    }, [ticketInfo]);

    const gameName = firstMarketData?.gameName || "";
    const currencyCode = firstMarketData?.currency || "";
    const roundId = firstMarketData?.roundId;

    const totalBetAmount = useMemo((): number => {
      if (isExpress) {
        return amount;
      }
      const total = ticketInfo.reduce<number>((acc, curr) => {
        return acc + +(curr.betAmount || 0);
      }, 0);
      return parseNumberByPrecision(1, total);
    }, [ticketInfo, isExpress, amount]);

    const totalOdd = useMemo(() => {
      const odd = ticketInfo.reduce<number>((acc, market) => {
        return acc * market.odds;
      }, 1);
      return truncWithPrecision(odd);
    }, [ticketInfo]);

    const handleTicketLoad = useCallback(() => {
      setTicketLoaded(ticketConfig?.showLogo || true);
    }, []);

    const handleQrImgLoad = useCallback(() => {
      setQrImgLoaded(true);
    }, []);

    const totalPossibleWin = useMemo(
      () =>
        possibleWin ||
        ticketInfo?.reduce(
          (acc, currentValue) => currentValue.possibleWin + acc,
          0,
        ),
      [possibleWin, ticketInfo],
    );

    return (
      <Ticket
        barcode={barcode}
        onTicketLoad={handleTicketLoad}
        showLogo={ticketConfig?.showLogo}
        logo={ticketConfig?.logo}
        setImgImageError={setImgImageError}
        handleQrImgLoad={handleQrImgLoad}
      >
        {ticketConfig?.shortTicket ? <h1>{gameName}</h1> : null}

        {!ticketConfig?.shortTicket ? (
          <>
            <BetInfo
              date={date}
              barCode={barcode}
              ticketId={ticketId}
              userName={userName}
              betTypeName={betType ? BetTypeNames[betType] : ""}
              showUserName={ticketConfig?.showUserName}
            />
            {ticketInfo.map(
              (
                {
                  betAmount,
                  odds,
                  marketName,
                  currency,
                  gameName,
                  winAmount,
                  combinations,
                  betType,
                  possibleWin,
                  translationSymbols,
                  possibleCombinations,
                  roundId,
                  eventName,
                  numbers,
                },
                index,
              ) => (
                <Market
                  odd={odds}
                  combinations={combinations}
                  betType={betType}
                  key={marketName}
                  roundId={roundId}
                  gameName={gameName}
                  betAmount={betAmount}
                  isExpress={isExpress}
                  possibleCombinations={possibleCombinations}
                  eventName={
                    translationSymbols?.homeTeam
                      ? `${ReplaceVirtualTranslationKey(
                          t(translationSymbols?.homeTeam),
                          translationSymbols,
                          t,
                        )} - ${ReplaceVirtualTranslationKey(
                          t(translationSymbols?.awayTeam),
                          translationSymbols,
                          t,
                        )}`
                      : ""
                  }
                  marketName={
                    numbers ||
                    ReplaceVirtualTranslationKey(
                      t(translationSymbols?.marketName || marketName),
                      translationSymbols,
                      t,
                    )
                  }
                  selectionName={ReplaceVirtualTranslationKey(
                    t(translationSymbols?.selectionName),
                    translationSymbols,
                    t,
                  )}
                  handicap={translationSymbols?.handicap}
                  currencyCode={currency}
                  showLine={index !== ticketInfo.length - 1}
                  winAmount={winAmount}
                  showWinAmount={ticketConfig?.showExpectedWinPerBet}
                  possibleWin={possibleWin}
                  ticketConfig={ticketConfig}
                />
              ),
            )}
          </>
        ) : null}
        <BetTicketContentFooter
          betId={betId}
          gameId={gameId}
          totalOdd={totalOdd}
          selectedLanguageCode={selectedLanguageCode}
          isExpress={isExpress}
          possibleWin={totalPossibleWin}
          currencyCode={currencyCode}
          totalBetAmount={totalBetAmount}
          totalWinAmount={ticketInfo.reduce(
            (accumulator, currentValue) => accumulator + currentValue.winAmount,
            0,
          )}
          ticketConfig={ticketConfig}
        />
      </Ticket>
    );
  },
);

export default memo(BetTicket);
